// components/EmailWeeklyCalendar.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Stack,
  Chip,
  Tooltip,
} from "@mui/material";
import { ChevronLeft, ChevronRight, CalendarViewWeek } from "@mui/icons-material";
import { format, startOfWeek, addDays, isSameDay } from "date-fns";
import { emailDashboardApi } from "../services/emailDashboardApi";

const BORDER = "#E0E0E0";

// -------------------- helpers

const normalizeUtcIso = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s) return null;

  // already has timezone or Z — leave as-is
  if (/[zZ]$/.test(s) || /[+-]\d{2}:\d{2}$/.test(s)) return s;

  // "YYYY-MM-DD HH:mm:ss" -> "YYYY-MM-DDTHH:mm:ssZ" (backend stores UTC, no suffix)
  const withT = s.includes(" ") ? s.replace(" ", "T") : s;
  return withT + "Z";
};

// Prefer schedule_at (email backend uses schedule_at)
const pickScheduleIso = (camp) => {
  return (
    camp?.schedule_at ??
    camp?.scheduled_at ??
    camp?.scheduledAt ??
    camp?.scheduleAt ??
    null
  );
};


const dayKeyInTz = (rawIso) => {
  const iso = normalizeUtcIso(rawIso);
  if (!iso) return null;
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return null;
  const yyyy = dt.getFullYear();
  const mm   = String(dt.getMonth() + 1).padStart(2, "0");
  const dd   = String(dt.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const timeLabel = (rawIso) => {
  if (!rawIso) return "—";
  const iso = normalizeUtcIso(rawIso);
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return "—";
  const hh = String(dt.getHours()).padStart(2, "0");
  const mi = String(dt.getMinutes()).padStart(2, "0");
  return `${hh}:${mi}`;
};

// Email statuses mapping
const getColors = (status) => {
  switch (String(status || "").toLowerCase()) {
    case "scheduled":
      return { bg: "#FFF3E0", border: "#FF9800", chip: "warning" };
    case "queued":
    case "sending":
      return { bg: "#E3F2FD", border: "#2196F3", chip: "info" };
    case "sent":
    case "delivered":
    case "opened":
      return { bg: "#E8F5E9", border: "#4CAF50", chip: "success" };
    case "bounced":
    case "failed":
      return { bg: "#FFEBEE", border: "#E53935", chip: "error" };
    default:
      return { bg: "#FFFFFF", border: "#E0E0E0", chip: "default" };
  }
};

// ✅ Email calendar should show these statuses if backend returns them
const isEmailCalendarStatus = (camp) => {
  const s = String(camp?.status || "").toLowerCase();
  return [
    "scheduled",
    "queued",
    "sending",
    "sent",
    "delivered",
    "opened",
    "bounced",
    "failed",
  ].includes(s);
};

// normalize payload shapes
const normalizeCalendarPayload = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
};

export default function EmailWeeklyCalendar({
  showHeader = true,
  onSelectCampaign,
}) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [weekData, setWeekData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const startDate = useMemo(
    () => startOfWeek(currentDate, { weekStartsOn: 1 }),
    [currentDate]
  );

  const isWeekInPast = useMemo(() => {
    const endOfWeek = addDays(startDate, 6);
    const endOfWeekEnd = new Date(endOfWeek);
    endOfWeekEnd.setHours(23, 59, 59, 999);
    return endOfWeekEnd < new Date();
  }, [startDate]);

  const loadCalendarData = async () => {
    setLoading(true);
    setErr("");

    try {
      const startStr = format(startDate, "yyyy-MM-dd");
      const endStr = format(addDays(startDate, 6), "yyyy-MM-dd");

      // ✅ IMPORTANT: request ALL, not only scheduled
      const payload = await emailDashboardApi.getCalendar({
        start: startStr,
        end: endStr,
        only: "all",
        tz: "Africa/Harare",
      });

      const safe = normalizeCalendarPayload(payload);

      const days = Array.from({ length: 7 }).map((_, index) => {
        const dayDate = addDays(startDate, index);
        const dayStr = format(dayDate, "yyyy-MM-dd");

        // Each campaign carries a per-day `schedule[]` ({date, planned, sent}) from the
        // backend. A paced campaign expands to one card per pacing day; non-paced ones
        // have a single entry. We fall back to legacy schedule_at placement if absent.
        const dayItems = [];
        safe
          .filter((c) => isEmailCalendarStatus(c))
          .forEach((c) => {
            const sched = Array.isArray(c.schedule) ? c.schedule : null;
            if (sched && sched.length) {
              sched.forEach((entry, idx) => {
                if (entry?.date === dayStr) {
                  dayItems.push({ camp: c, entry, dayIndex: idx, dayCount: sched.length });
                }
              });
            } else if (pickScheduleIso(c) && dayKeyInTz(pickScheduleIso(c)) === dayStr) {
              dayItems.push({ camp: c, entry: null, dayIndex: 0, dayCount: 1 });
            }
          });

        dayItems.sort((a, b) => {
          const ad = new Date(normalizeUtcIso(pickScheduleIso(a.camp)));
          const bd = new Date(normalizeUtcIso(pickScheduleIso(b.camp)));
          return (ad?.getTime?.() || 0) - (bd?.getTime?.() || 0);
        });

        const endOfDay = new Date(dayDate);
        endOfDay.setHours(23, 59, 59, 999);
        const isPastDay = endOfDay < new Date();

        return {
          dateObj: dayDate,
          short: format(dayDate, "EEE"),
          isToday: isSameDay(dayDate, new Date()),
          isPastDay,
          items: dayItems,
        };
      });

      setWeekData(days);
    } catch (e) {
      console.error("Failed to load email calendar", e);
      setWeekData([]);
      setErr(e?.response?.data?.message || e?.message || "Failed to load calendar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCalendarData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate]);

  const nextWeek = () => setCurrentDate((d) => addDays(d, 7));
  const prevWeek = () => setCurrentDate((d) => addDays(d, -7));

  const canPlanInThisWeek = !isWeekInPast;

  return (
    <Box sx={{ width: "100%" }}>
      {showHeader && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={900}>
              Email Campaign Weekly Plan ({format(startDate, "MMM d")} -{" "}
              {format(addDays(startDate, 6), "MMM d, yyyy")})
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Calendar shows scheduled email campaigns (scheduled, queued, sending, sent, failed, etc).
              Click a card to view/edit.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip
              title={
                canPlanInThisWeek
                  ? "Refresh calendar"
                  : "This week is in the past. Move to a future week."
              }
            >
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<CalendarViewWeek />}
                  onClick={loadCalendarData}
                  sx={{ textTransform: "none", borderRadius: 999 }}
                >
                  Refresh
                </Button>
              </span>
            </Tooltip>

            <IconButton size="small" onClick={prevWeek}>
              <ChevronLeft />
            </IconButton>
            <IconButton size="small" onClick={nextWeek}>
              <ChevronRight />
            </IconButton>
          </Stack>
        </Box>
      )}

      {err && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="error">
            {err}
          </Typography>
        </Box>
      )}

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            p: 5,
            border: `1px solid ${BORDER}`,
            borderRadius: 2,
            bgcolor: "white",
            minHeight: 240,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(7, 1fr)" },
            border: `1px solid ${BORDER}`,
            borderRadius: 2,
            overflow: "hidden",
            width: "100%",
            bgcolor: "white",
            minHeight: 520,
          }}
        >
          {weekData.map((day, index) => (
            <Box
              key={index}
              sx={{
                minHeight: 520,
                borderRight: { md: index < 6 ? `1px solid ${BORDER}` : "none" },
                borderBottom: { xs: `1px solid ${BORDER}`, md: "none" },
                bgcolor: day.isToday ? "#FFF9C4" : day.isPastDay ? "#FAFAFA" : "#FFFFFF",
                opacity: day.isPastDay ? 0.85 : 1,
              }}
            >
              <Box
                sx={{
                  p: 1.25,
                  bgcolor: day.isToday ? "#FFFDE7" : "#F5F5F5",
                  borderBottom: `1px solid ${BORDER}`,
                  textAlign: "center",
                }}
              >
                <Typography variant="subtitle2" fontWeight={900} color="text.secondary">
                  {day.short}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {format(day.dateObj, "MMM d")}
                </Typography>
              </Box>

              <Box sx={{ p: 1 }}>
                {day.items?.length ? (
                  day.items.map(({ camp, entry, dayIndex, dayCount }) => {
                    const colors = getColors(camp?.status);
                    const iso = pickScheduleIso(camp);
                    const isPaced = dayCount > 1 || camp?.pacing_mode === "spread";

                    // Per-day pacing entry: show ACTUAL sent once a slice has gone out
                    // (today/past), otherwise the PLANNED slice for that day.
                    let recipientsLabel;
                    let isProjection = false;
                    if (entry) {
                      const planned = Number(entry.planned ?? 0);
                      const sent = Number(entry.sent ?? 0);
                      if ((day.isPastDay || day.isToday) && sent > 0) {
                        recipientsLabel = `${sent.toLocaleString()} sent`;
                      } else {
                        recipientsLabel = `~${planned.toLocaleString()} planned`;
                        isProjection = true;
                      }
                    } else {
                      // Legacy fallback (campaign without a schedule[]).
                      const actualTotal = (camp?.counts?.total ?? 0) ||
                        ((camp?.counts?.queued ?? 0) + (camp?.counts?.sending ?? 0) +
                         (camp?.counts?.sent ?? 0) + (camp?.counts?.delivered ?? 0) +
                         (camp?.counts?.opened ?? 0) + (camp?.counts?.bounced ?? 0) +
                         (camp?.counts?.failed ?? 0));
                      const expectedTotal = camp?.totals_json?.expected ?? 0;
                      const isPendingDispatch = actualTotal === 0 && camp?.scope === "filter_all" && camp?.status === "scheduled";
                      const recipients = isPendingDispatch ? expectedTotal : actualTotal;
                      recipientsLabel = isPendingDispatch
                        ? `~${recipients.toLocaleString()} (pending dispatch)`
                        : recipients.toLocaleString();
                    }

                    return (
                      <Tooltip
                        key={`${camp?.id ?? index}-${entry?.date ?? iso}`}
                        title="Click to view/edit"
                        placement="top"
                      >
                        <Paper
                          elevation={0}
                          onClick={() => onSelectCampaign?.(camp)}
                          sx={{
                            p: 1.25,
                            mb: 1.25,
                            bgcolor: colors.bg,
                            border: isProjection ? "1px dashed" : "1px solid",
                            borderColor: colors.border,
                            borderRadius: 2,
                            cursor: "pointer",
                            "&:hover": { boxShadow: 2 },
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="caption" color="text.secondary">
                              {iso ? timeLabel(iso) : "—"}
                            </Typography>

                            <Chip
                              size="small"
                              label={String(camp?.status || "")}
                              color={colors.chip}
                              sx={{ textTransform: "capitalize", fontWeight: 800 }}
                            />
                          </Stack>

                          <Typography variant="subtitle2" fontWeight={900} sx={{ mt: 0.75 }}>
                            {camp?.title ?? camp?.name ?? camp?.subject ?? "Untitled Campaign"}
                          </Typography>

                          {isPaced && (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`paced · day ${dayIndex + 1}/${dayCount}`}
                              sx={{ mt: 0.5, mb: 0.25, height: 20, fontSize: 11, fontWeight: 700 }}
                            />
                          )}

                          <Typography variant="caption" display="block" color="text.secondary">
                            <strong>Recipients:</strong> {recipientsLabel}
                          </Typography>
                        </Paper>
                      </Tooltip>
                    );
                  })
                ) : (
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ display: "block", textAlign: "center", mt: 6 }}
                  >
                    No Email Campaigns
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}


